'use client';

import { Info } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/lib/contexts/LanguageContext';

import type { JSX } from 'react';

interface CompanySignatureProps {
  formData: {
    signatureType: string;
    signatureDetails: string;
    [key: string]: string | boolean | string[];
  };
  onChange: (section: string, field: string, value: string | boolean | string[]) => void;
}

export default function CompanySignaturePage({
  formData,
  onChange,
}: Readonly<CompanySignatureProps>): JSX.Element {
  const { t } = useLanguage();

  const handleSignatureTypeChange = (value: string) => {
    onChange('companySignature', 'signatureType', value);
    // Clear details when switching to board-only option
    if (value === 'board') {
      onChange('companySignature', 'signatureDetails', '');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-lg font-medium">{t.companySignatureTitle}</h3>
        <div className="mb-6 flex items-center gap-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t.companySignatureDescription}
          </p>
          <Info className="h-4 w-4 text-green-600 dark:text-green-400" />
        </div>
      </div>

      <RadioGroup
        value={formData.signatureType || 'board'}
        onValueChange={handleSignatureTypeChange}
        className="space-y-4"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="board" id="signatureBoard" />
          <Label htmlFor="signatureBoard" className="cursor-pointer">
            {t.signatureBoard}
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <RadioGroupItem value="custom" id="signatureCustom" />
          <Label htmlFor="signatureCustom" className="cursor-pointer">
            {t.signatureCustom}
          </Label>
        </div>
      </RadioGroup>

      {formData.signatureType === 'custom' && (
        <div className="mt-6 space-y-2">
          <Textarea
            value={formData.signatureDetails || ''}
            onChange={(e) => onChange('companySignature', 'signatureDetails', e.target.value)}
            className="min-h-32 w-full"
            placeholder={t.signatureDetailsPlaceholder}
          />
        </div>
      )}
    </div>
  );
}
